import { Injectable, NgZone } from '@angular/core';
import { IUser } from '../models/user';

import { addDoc, collection, Firestore } from '@angular/fire/firestore';
import {
  Auth, getAuth, User, onAuthStateChanged, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, updateProfile,
  UserCredential, signOut, authState, IdTokenResult,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithCredential
} from '@angular/fire/auth';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserService } from './dal/user.service';
import { SubscriptionHandler } from './subscriptionHandler.service';
import { dbConst, regionConst } from './dal/databaseConstants';
import { getApp } from '@angular/fire/app';
import { Capacitor } from '@capacitor/core';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})

export class AuthService {
  constructor(
    public firestore: Firestore, // Inject Firestore service
    public afAuth: Auth, // Inject Firebase auth service
    private subscriptionHandler: SubscriptionHandler,
    private userService: UserService,
    private toastService: ToastService,
    public router: Router,
    public ngZone: NgZone,
  ) {



    // onAuthStateChanged(auth, (user) => {
    //   if (user) {
    //     // User is signed in, see docs for a list of available properties
    //     // https://firebase.google.com/docs/reference/js/firebase.User
    //     const uid = user.uid;
    //     // ...
    //   } else {
    //     // User is signed out
    //     // ...
    //   }
    // });


    // this.afAuth.authState.subscribe( authState => {
    //   this.authState = authState;

    // });

    this.getAuthState();
    this.onAuthStateChanged();
  }

  refreshTime!: number;
  authState: any = null;

  user$!: Observable<User | undefined | null>;
  user!: User;
  barId!: string;

  public userId!: string;

  // get;
  // boolean;

  //#region NgRx
  selectUserFromAuthState() {
    const auth = getAuth();
    return authState(auth);
  }


  selectBarId(): Observable<string> {
    const auth = getAuth();

    const barIdSubject = new BehaviorSubject<string>('');
    const barId$ = barIdSubject.asObservable();

    onAuthStateChanged(auth, (user) =>
      user?.getIdTokenResult(true).then((result: IdTokenResult) => {
        const barId = result.claims['barOwner'] != null && (result.claims['barOwner'] as string[]).length > 0
          ? (result.claims['barOwner'] as string[])[0]
          : '';

        barIdSubject.next(barId)

      })
    );

    return barId$;

  }



  async selectBarIdFromFunction() {
    const functions = getFunctions(getApp(), regionConst.europeWest1);
    const callable = httpsCallable(functions, 'selectBarId');
    const retValue = await callable();
    return retValue;
  }


  async selectUserHasPremium(barId: string): Promise<boolean> {
    const claims = await this.selectUserTokenClaim();
    const subs = claims?.claims['subs'] as any[];
    const bar = subs.find(x => x.barId == barId);
    if (!(bar && bar.expDate)) {
      return false;
    }
    const expDate = new Date(bar.expDate);
    return expDate >= new Date();
  }

  async selectUserSubscriptions(): Promise<any[]> {
    const claims = await this.selectUserTokenClaim();
    const subs = claims?.claims['subs'] as any[];
    return subs;
  }

  //#endregion NgRx






  selectUser(skipLocalStorage = false): User | undefined {
    if (localStorage.getItem('user') && !skipLocalStorage) {
      const user = JSON.parse(localStorage.getItem('user') ?? '') as User;
      return user;
    }
    const auth = getAuth();
    if (auth.currentUser) {
      return auth.currentUser;
    }
    return undefined;


  }



  reloadUser(): Promise<void> | undefined {
    const auth = getAuth();

    return auth?.currentUser?.reload();
  }

  async loginWithGoogle() {
    const auth = getAuth();

    return await signInWithPopup(auth, new GoogleAuthProvider());
  }


  async loginWithGoogleNative() {
    try {
      const platform = Capacitor.getPlatform();
      const loginOptions: any = {
        provider: 'google',
        options: {}
      };

      // Platform-specific options
      if (platform === 'android') {
        loginOptions.options.forceRefreshToken = true; // Android only: Force new authentication
        loginOptions.options.filterByAuthorizedAccounts = false; // Android only: Allow all accounts (fixes reauth errors)
        loginOptions.options.style = 'standard'; // Use standard style instead of bottom sheet (more reliable)
      } else if (platform === 'ios') {
        loginOptions.options.scopes = ['profile', 'email']; // iOS only
      }

      const result = await SocialLogin.login(loginOptions);

      if (result && result.result) {
        const googleResult = result.result as any;
        if (googleResult.idToken) {
          const auth = getAuth();
          // accessToken is an AccessToken object with a .token property, and may be null on Android (Credential Manager)
          const accessTokenString = googleResult.accessToken?.token || null;
          const credential = GoogleAuthProvider.credential(googleResult.idToken, accessTokenString);
          return await signInWithCredential(auth, credential);
        }
      }
      throw new Error('No ID token received from Google');
    } catch (error) {
      throw error;
    }
  }

  async loginWithFacebook() {
    const auth = getAuth();
    const provider = new FacebookAuthProvider();
    provider.addScope('email');
    provider.addScope('public_profile');
    return await signInWithPopup(auth, provider);
  }

  async loginWithFacebookNative() {
    try {
      const platform = Capacitor.getPlatform();
      // Generate a random nonce for iOS Limited Login (required by Firebase for OIDC)
      const rawNonce = this.generateNonce();

      const loginOptions: any = {
        provider: 'facebook',
        options: {
          permissions: ['email', 'public_profile'],
        }
      };

      if (platform === 'ios') {
        // Clear any cached Facebook session so logIn() always creates a fresh
        // AuthenticationToken with the new nonce. Without this, the SDK may
        // silently return a cached token whose nonce doesn't match rawNonce,
        // causing Firebase auth/missing-or-invalid-nonce.
        try {
          await SocialLogin.logout({ provider: 'facebook' });
        } catch (e) { /* ignore — no existing session */ }

        // Facebook Limited Login requires a SHA-256-hashed nonce; Firebase verifies by hashing rawNonce itself.
        const hashedNonce = await this.sha256(rawNonce);
        loginOptions.options.limitedLogin = true;
        loginOptions.options.nonce = hashedNonce;
      }

      const result = await SocialLogin.login(loginOptions);
      if (result && result.result) {
        const fbResult = result.result as any;
        const auth = getAuth();
        let credential;

        if (platform === 'ios' && fbResult.idToken) {
          const provider = new OAuthProvider('facebook.com');
          credential = provider.credential({
            idToken: fbResult.idToken,
            rawNonce: rawNonce
          });
        } else if (fbResult.accessToken) {
          credential = FacebookAuthProvider.credential(fbResult.accessToken.token);
        }

        if (credential) {
          return await signInWithCredential(auth, credential);
        }
      }
      throw new Error('No access token received from Facebook');
    } catch (error) {
      throw error;
    }
  }

  private generateNonce(length: number = 32): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const values = new Uint8Array(length);
    crypto.getRandomValues(values);
    return Array.from(values, (v) => charset[v % charset.length]).join('');
  }

  private async sha256(message: string): Promise<string> {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  async loginWithApple() {
    const auth = getAuth();
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return await signInWithPopup(auth, provider);
  }

  async loginWithAppleNative() {
    try {
      const loginOptions: any = {
        provider: 'apple',
        options: {
          scopes: ['email', 'name']
        }
      };

      const result = await SocialLogin.login(loginOptions);

      if (result && result.result) {
        const appleResult = result.result as any;
        if (appleResult.idToken) {
          const auth = getAuth();
          const provider = new OAuthProvider('apple.com');
          const credential = provider.credential({
            idToken: appleResult.idToken,
            rawNonce: appleResult.nonce
          });
          return await signInWithCredential(auth, credential);
        }
      }
      throw new Error('No id token received from Apple');
    } catch (error) {
      throw error;
    }
  }


  async login(
    email: string,
    password: string
  ): Promise<UserCredential> {

    const auth = getAuth();
    return await signInWithEmailAndPassword(auth, email, password);
  }

  async signup(
    displayName: string,
    email: string,
    password: string
  ): Promise<UserCredential> {
    try {

      const auth = getAuth();
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(cred.user);
      await updateProfile(cred.user, { displayName: displayName });


      const entity: IUser = {
        uid: cred.user.uid,
        displayName: displayName,
        email: cred.user.email ?? undefined,
        emailVerified: cred.user.emailVerified,
        photoURL: ''
      };


      const collRef = collection(this.firestore, dbConst.users);
      await addDoc(collRef, entity);

      return cred;
    } catch (error) {
      throw error;
    }
  }

  resetPassword(email: string): Promise<void> {
    const auth = getAuth();

    return sendPasswordResetEmail(auth, email);
  }

  async logout(): Promise<void> {
    const auth = getAuth();

    this.subscriptionHandler.clearSubscriptions();
    localStorage.clear();

    // Clear Firestore offline cache before signing out
    try {
      const dbs = await window.indexedDB.databases();
      for (const db of dbs) {
        if (db.name?.startsWith('firestore/')) {
          await window.indexedDB.deleteDatabase(db.name);
        }
      }
    } catch (e) { /* ignore - databases() not supported in all browsers */ }

    // Logout from native social providers if available
    try {
      await SocialLogin.logout({ provider: 'google' });
    } catch (error) {
      console.info('Native Google logout not needed or failed:', error);
    }

    try {
      await SocialLogin.logout({ provider: 'apple' });
    } catch (error) {
      console.info('Native Apple logout not needed or failed:', error);
    }

    return signOut(auth);
  }


  // // Sign in with Google
  // googleAuthenticate(): Promise<firebase.auth.UserCredential> {
  //   return this.afAuth.signInWithPopup(new auth.GoogleAuthProvider());
  // }

  // // Sign in with Google
  // facebookAuthenticate(): Promise<firebase.auth.UserCredential> {
  //   return this.afAuth.signInWithPopup(new auth.FacebookAuthProvider());
  // }

  onAuthStateChanged() {
    const callback = null;
    const metadataRef = null;
    const auth = getAuth();



    onAuthStateChanged(auth, (user) => {

      // this.store.dispatch(UserActions.loadUser({user: user}));

      // On user login add new listener.
      if (user) {
        // Check if refresh is required.
        this.userService.getMetadataUser(user.uid).subscribe((data) => {

          if (!data) {
            this.userService.setMetaDataUser(user.uid);
            return;
          }


          const refreshTime = localStorage.getItem('refreshTime');
          const barId = localStorage.getItem('barId');

          // if(refreshTime == data.refreshTime && barId)
          // {

          //   this.subscriptionService.userHasPremium(this.barId, false);
          //   return;
          // }
          // else {
          //   localStorage.setItem('refreshTime', data.refreshTime);
          //   localStorage.setItem('barId', this.barId);
          //   user.getIdToken(true).catch((error) => {
          //     console.error('getTokenError', error);
          //   });
          //   this.subscriptionService.userHasPremium(this.barId, true);

          // }
        });
      }
    });
  }


  selectUserTokenClaim(): Promise<IdTokenResult> | undefined {
    const auth = getAuth();
    return auth.currentUser?.getIdTokenResult();
  }


  selectUserBarId(): Promise<string> {

    const promise = new Promise<string>(async (resolve, reject) => {
      if (this.barId) {
        resolve(this.barId);
      }
      else {
        const auth = getAuth();
        if (!auth.currentUser) {
          this.getAuthState();
          // Bounded polling: wait at most ~10s (20 × 500ms) for barId to appear
          // in localStorage after auth init. The previous unbounded while loop could
          // spin indefinitely (battery), and the reject below was dead code.
          for (let attempt = 0; attempt < 20 && !this.barId; attempt++) {
            await new Promise(f => setTimeout(f, 500));
            this.barId = localStorage.getItem('barId') ?? '';
          }
          if (!this.barId) {
            reject('Unable to get barId')
          } else {
            resolve(this.barId);
          }
        } else {
          this.barId = await this.selectBarIdFromTokenResult() ?? '';
          if (!this.barId) {
            reject('Unable to get barId')
          } else {
            resolve(this.barId);
          }



          // auth.currentUser.getIdTokenResult()
          // .then((result) => {

          //   this.barId =
          //     result.claims.barOwner != null && result.claims.barOwner.length > 0
          //       ? result.claims.barOwner[0]
          //       : null;
          //   resolve(this.barId);
          // })
          // .catch(error => {
          //   this.router.navigateByUrl('/login');
          //   reject(error);
          // });
        }

      }


    });
    return promise;
  }


  async selectBarIdFromTokenResult() {
    const auth = getAuth();
    if (auth) {
      const barId = await auth.currentUser?.getIdTokenResult()
        .then((result) => {
          return result.claims['barOwner'] != null && (result.claims['barOwner'] as string[]).length > 0
            ? (result.claims['barOwner'] as string[])[0]
            : null;
        })
        .catch(error => {
          this.router.navigateByUrl('/login');
          return null;
        });
      return barId;
    }
    return null
  }

  getAuthState() {
    const auth = getAuth() as any;

    this.user$ = authState(auth);
    const ret$ = this.user$.subscribe((user) => {
      if (user) {
        this.user = user;
        localStorage.setItem('user', JSON.stringify(this.user));
      } else {
        // Do not call logout() here — it would trigger a second signOut() while
        // Firestore is still processing the first one, corrupting its IndexedDB state
        // and causing subsequent setDoc/addDoc calls to hang on re-login.
        localStorage.setItem('user', '');
        this.subscriptionHandler.clearSubscriptions();
        this.router.navigate(['login']);
      }
    });
    return this.user$;
  }



  isLoggedIn() {
    const userStr = localStorage?.getItem('user');
    if (!userStr) return false;
    try {
      const user = JSON.parse(userStr);
      return user !== null && user !== '';
    } catch {
      return false;
    }
  }

  // Sign in with email/password
  SignIn(email: string, password: string) {

    const auth = getAuth();

    return signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        return userCredential;
        // Signed in
        const user = userCredential.user;
        // ...
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        window.alert(error.message);
      });
  }

  // Sign up with email/password
  SignUp(email: string, password: string) {
    const auth = getAuth();

    return createUserWithEmailAndPassword(auth, email, password)
      .then(async (result) => {
        /* Call the SendVerificaitonMail() function when new user sign
        up and returns promise */
        await this.sendVerificationMail();
      })
      .catch((error) => {
        window.alert(error.message);
      });
  }

  // Send email verfificaiton when new user sign up
  sendVerificationMail() {
    const auth = getAuth();
    if (!!auth.currentUser) {
      sendEmailVerification(auth.currentUser)
        .then(() => {
          this.router.navigate(['verify-email']);
        })
    }
  }



  // Reset Forggot password
  ForgotPassword(passwordResetEmail: string) {
    const auth = getAuth();
    sendPasswordResetEmail(auth, passwordResetEmail)
      .then(() => {
        window.alert('Password reset email sent, check your inbox.');
      })
      .catch((error) => {
        window.alert(error);
      });
  }

  // // Sign in with Google
  // GoogleAuth() {
  //   return this.AuthLogin(new auth.GoogleAuthProvider());
  // }

  // // Sign in with Google
  // FacebookAuth() {
  //   this.afAuth.auth.setPersistence('local');

  //   return this.AuthLogin(new auth.FacebookAuthProvider());
  // }

  // Auth logic to run auth providers
  // AuthLogin(provider) {
  //   return this.afAuth.auth
  //     .signInWithPopup(provider)
  //     .then((result) => {
  //       this.ngZone.run(() => {
  //         this.router.navigate(['home']);
  //       });
  //     })
  //     .catch((error) => {
  //       window.alert(error);
  //     });
  // }

  impersonate(barId: string): Promise<any> {
    const functions = getFunctions(getApp(), regionConst.europeWest1);
    const callable = httpsCallable(functions, 'impersonateBar');
    return callable({
      barId
    });
  }

}
function typeOf(expDate: any): any {
  throw new Error('Function not implemented.');
}

